import { useMemo } from 'react';
import { parse } from 'date-fns';

import { API_DATE_FORMAT } from '../../../../utils';
import { useFlightsScheduleQuery, SegmentSearchParameter } from '@websky/graphql';
import { ApolloError } from 'apollo-boost';
import { isApolloErrorGuard } from './utils';
import { useConfig } from '../../../../context';
import { useTranslation } from 'react-i18next';

export const useAvailableDates = (segment?: SegmentSearchParameter) => {
	const { data: availableDates, refetch, error, loading } = useFlightsScheduleQuery({
		variables: {
			parameters: {
				departure: segment?.departure.iata,
				arrival: segment?.arrival.iata
			}
		},
		skip: !segment
	});

	const dates = useMemo(() => {
		if (availableDates?.FlightsSchedule.datesData) {
			return availableDates?.FlightsSchedule.datesData
				.filter(date => date?.date)
				.map(date => parse(date.date, API_DATE_FORMAT, new Date()));
		}

		return [];
	}, [availableDates?.FlightsSchedule]);

	return {
		dates,
		isLoading: loading,
		error,
		refetch
	};
};

export enum ExchangeErrorEnum {
	OutdatedRequest,
	NotAvailable,
	CheckedNotPermitted,
	UsedNotPermitted,
	ManualMode
}

export const useHtmlDescriptionOfError = (
	errors: ApolloError | string[],
	overrides?: { [error: string]: { header?: string; body?: string; status?: ExchangeErrorEnum } }
) => {
	const {
		Engine: { checkInURL, checkInEnabled }
	} = useConfig();
	const { t } = useTranslation('Exchange');

	const error = isApolloErrorGuard(errors) ? errors.graphQLErrors?.[0]?.message : errors?.[0];

	if (!error) {
		return null;
	}

	if (overrides?.[error]) {
		return overrides[error];
	}

	if (error.includes('This operation is not available') || error.includes('Order state has been changed')) {
		return {
			status: ExchangeErrorEnum.OutdatedRequest
		};
	}

	if (error.includes('Exchange operations with checked segments are not permitted')) {
		return {
			header: t(`It is not possible to exchange the ticket on which the check-in was made for the flight`),
			body: checkInEnabled
				? `${t('If you wish to exchange your ticket, please cancel your flight check-in.')}
				${t(`You can do it yourself by going to <a href='{{checkInURL}}' target='_blank'>Online check-in</a>.`, { checkInURL })}
				${t('Either select unused ticket segments for exchange.')}`
				: `${t('If you wish to exchange your ticket, please cancel your flight check-in.')}
				${t('Either select unused ticket segments for exchange.')}`,
			status: ExchangeErrorEnum.CheckedNotPermitted
		};
	}

	if (error.includes('Exchange operations with used segments are not permitted')) {
		return {
			header: t(`It is not possible to exchange a used ticket`),
			body: t('The ticket has been flown on, you cannot exchange the used ticket'),
			status: ExchangeErrorEnum.UsedNotPermitted
		};
	}

	if (error.includes('Exchange operation allowed only in manual mode')) {
		return {
			header: t('Partially used ticket can be exchanged only in manual mode'),
			body: t(
				`Contact the airline call-center to clarify the conditions of the exchange, as a part of the route was flown. Or select segments of an unused ticket for exchange.`
			),
			status: ExchangeErrorEnum.ManualMode
		};
	}
	if (error.includes('All sectors in new itineraty are flown. No exchange allowed')) {
		return {
			body: t(`All sectors in new itineraty are flown. No exchange allowed`),
			status: ExchangeErrorEnum.NotAvailable
		};
	}

	return null;
};
